import { Plugin, PluginKey, TextSelection } from "prosemirror-state";
import { EditorView } from "prosemirror-view";

import { schema } from "../adapter";

import { collaborationUploadFile } from "api/api-worksheets";
import { t } from "i18n/i18n";
import { toastLocalizedOperationCatcher } from "utils/alerts";
import { rollbarAndLogError } from "utils/logger";

/**
 * Display the overlay while image is uploading
 *
 * @param wrapperElement - The wrapper element containing the editor
 * @returns The overlay element, or null if it couldn't be created
 */
function addUploadOverlay(wrapperElement: HTMLElement): HTMLElement | null {
  let overlayElement = wrapperElement.querySelector<HTMLElement>(".prosemirror-upload-overlay");
  if (!overlayElement) {
    overlayElement = document.createElement("div");
    overlayElement.className =
      "prosemirror-upload-overlay " +
      "animate-fade-out animate-fade-out-trigger " +
      "pointer-events-auto " +
      "absolute bottom-0 left-0 right-0 top-0 " +
      "flex items-center justify-center " +
      "text-white bg-black/70 rounded-md " +
      "z-[1001]"; // Above the toolbar

    const text = document.createElement("p");
    text.className = "body";
    text.textContent = t("worksheet.rich_text_editor.uploading_image");
    overlayElement.appendChild(text);

    wrapperElement.appendChild(overlayElement);
  }

  return overlayElement;
}

export const handleImageUpload = async (file: File, view: EditorView) => {
  const wrapperElement = view.dom.closest(".prosemirror-editor-wrapper") as HTMLElement | null;
  if (!wrapperElement) {
    rollbarAndLogError("[ProseMirrorEditor] Wrapper element not found for image upload");
    return;
  }

  const overlay = addUploadOverlay(wrapperElement);

  try {
    // Upload the file
    const response = await collaborationUploadFile(file);

    // Get the current state (may have changed since button was clicked)
    const currentState = view.state;
    const currentFrom = currentState.selection.from;

    // Insert the image at the current selection position
    const imageNode = schema.nodes.image.create({ src: response.url });
    const tr = currentState.tr.insert(currentFrom, imageNode);

    // Move cursor after the inserted image (image node has size 1)
    const newPos = currentFrom + 1;
    const resolvedPos = tr.doc.resolve(newPos);
    tr.setSelection(TextSelection.near(resolvedPos));

    view.dispatch(tr);
  } catch (error) {
    rollbarAndLogError("[ProseMirrorEditor] Error uploading image", error);
    toastLocalizedOperationCatcher("image_upload_failure")(error);
  } finally {
    overlay?.remove();
  }
};

function handleDragOver(event: DragEvent) {
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) return false;
  const imageItems = [...dataTransfer.items].filter(
    (item) => item.kind === "file" && item.type.startsWith("image/"),
  );
  if (imageItems.length > 0) {
    event.preventDefault();
  } else if (dataTransfer.types.includes("Files")) {
    // eslint-disable-next-line no-param-reassign
    event.dataTransfer.dropEffect = "none";
  }
}

function handleDrop(view: EditorView, event: DragEvent) {
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) return false;
  const imageItems = [...dataTransfer.items].filter(
    (item) => item.kind === "file" && item.type.startsWith("image/"),
  );
  const file = imageItems[0]?.getAsFile();
  if (file) {
    handleImageUpload(file, view);
    event.preventDefault();
    return true;
  }
}

export function imageDropPlugin(): Plugin {
  return new Plugin({
    key: new PluginKey("image-drop"),

    // According to the ProseMirror documentation, we *should* be able to attach these event handlers
    // through `props.handleDOMEvents`, but it doesn't seem to be working.
    // As a workaround, we add a native event listeners directly to the editor's DOM element when the plugin is initialized.
    view(editorView) {
      const dropHandler = (event: DragEvent) => handleDrop(editorView, event);

      editorView.dom.addEventListener("dragover", handleDragOver);
      editorView.dom.addEventListener("drop", dropHandler);
      return {
        destroy() {
          editorView.dom.removeEventListener("dragover", handleDragOver);
          editorView.dom.removeEventListener("drop", dropHandler);
        },
      };
    },
  });
}
